import React from "react";
import YouTube, {YouTubePlayer} from "react-youtube";
import styles from './MediaSource.module.scss';
import {PlayerEventBus} from "../../playerEventBus";
import {NowPlaying, PlayerStatus} from "../../../../../store/slices/player/types";
import {updateNowPlaying, updateStatus, updateTotalDuration} from "../../../../../store/slices/player/actions";
import {usePlayerDispatch, usePlayerSelector} from "../../../../../store/slices/player/playerSlice";
import {useCastSelector} from "../../../../../store/slices/cast/castSlice";
import {Options} from "youtube-player/dist/types";
import {Song} from "../../../../../store/api/types";
import {shuffleTracks} from "../../utils";
import {ScrollingText} from "../../../../common/scrollingText/ScrollingText";

/* https://developers.google.com/youtube/player_parameters#playsinline */
const options: Options = {
    height: '60',
    width: '60',
    playerVars: {
        rel: 0, // don't show related videos
        controls: 0, // player controls do not display in the player
        disablekb: 1, // causes the player to not respond to keyboard controls
        fs: 0, // prevents the fullscreen button from displaying in the player
        playsinline: 1,
        origin: 'https://www.youtube.com',
        enablejsapi: 1, // enables the player to be controlled via IFrame
        iv_load_policy: 3, // causes video annotations to not be shown by default
    }
};

export interface MediaSourceProps {
    isFullScreen: boolean;
    hideVisuals?: boolean;
}

export interface MediaSourceRef {
    // The type of the ref can be specified as YouTubePlayer (assuming YouTubePlayer is the type of the player instance)
    player: YouTubePlayer | null;
}

export const MediaSource = React.forwardRef<MediaSourceRef, MediaSourceProps>(({isFullScreen, hideVisuals}, ref) => {
    const {status, nowPlaying, isInitiatedByPlayer, shuffle} = usePlayerSelector(['status', 'nowPlaying', 'isInitiatedByPlayer', 'shuffle']);
    const {state: castState} = useCastSelector(['state']);
    const isCasting = castState === 'started';
    // Synchronous read for PlayerEventBus callbacks — they shouldn't rebind every
    // time the cast state flips, so the subscribers check this ref at fire time.
    const isCastingRef = React.useRef(isCasting);
    React.useEffect(() => { isCastingRef.current = isCasting; }, [isCasting]);
    const [mediaSource, setMediaSource] = React.useState<YouTubePlayer>();
    const [shuffledList, setShuffledList] = React.useState<Song[]>([]);
    // If MediaSource mounts with a song already in state but status was reset to 'Initial'
    // (status is blacklisted from redux-persist), this is a redux-persist rehydration and
    // we must cue (not load) the video — loading would auto-play on app launch and
    // surprise the user. A fresh user click goes through updateNowPlaying which sets
    // status='Playing' before MediaSource mounts, so the captured ref stays undefined
    // and the effect falls through to load.
    //
    // We pin the *object reference*, not the song id: any subsequent updateNowPlaying
    // dispatch (including clicking the same song again) produces a new reference, so
    // restart-on-reclick still loads correctly.
    const rehydratedNowPlaying = React.useRef<NowPlaying | undefined>(
        !!nowPlaying && status === 'Initial' ? nowPlaying : undefined
    );
    const isFirstLoad = React.useRef(true);
    const playerDispatch = usePlayerDispatch();

    const shufflePrev = React.useRef<boolean>(shuffle);
    const showOverlay = status !== 'Playing' && nowPlaying?.playlist.img;

    const shufflePlaylist = React.useCallback((current: NowPlaying, forceShuffle = false) => {
        if ((!isInitiatedByPlayer && shuffle) || forceShuffle || isFirstLoad.current) {
            setShuffledList(shuffleTracks(current.playlist.tracks));
        }
    },[shuffle, isInitiatedByPlayer]);

    React.useEffect(() => {
        shufflePlaylist(nowPlaying!, !shufflePrev.current && shuffle)
        shufflePrev.current = shuffle;
    }, [nowPlaying, shuffle, shufflePlaylist]);

    React.useEffect(() => {
        const initiateNowPlaying = async () => {
            const songId = nowPlaying?.song?.id;
            if (!songId || !mediaSource) {
                return;
            }
            // While casting, the receiver owns playback. Cue locally so resuming
            // on the phone after disconnect lands on the right track instead of
            // a stale one — but never auto-play, that would double up the audio.
            if (isCastingRef.current || nowPlaying === rehydratedNowPlaying.current) {
                await mediaSource.cueVideoById(songId);
            } else {
                await mediaSource.loadVideoById(songId);
            }
            isFirstLoad.current = false;
        }
        initiateNowPlaying();

    }, [nowPlaying, mediaSource]);

    const handleNextPrevVideo = React.useCallback((isNextVideo: boolean) => {
        if(!nowPlaying) {
            return;
        }

        const playlist = shuffle ? shuffledList : nowPlaying.playlist.tracks;
        const currentIndex: number = playlist.findIndex((song: Song) => song.id === nowPlaying.song.id);
        const playlistLength = playlist.length;
        let indexToPlay: number = isNextVideo ? currentIndex + 1 : currentIndex - 1;
        if (indexToPlay < 0) {
            indexToPlay = playlistLength - 1;
        } else if (indexToPlay >= playlistLength) {
            indexToPlay = 0;
        }
        playerDispatch(updateNowPlaying({nowPlaying: {song: playlist[indexToPlay], playlist: nowPlaying.playlist}, isInitiatedByPlayer: true}))
    }, [nowPlaying, playerDispatch, shuffledList, shuffle]);

    const handlenError = (e: any) => {
        if (mediaSource && nowPlaying) {
            const fvd: string = e?.target?.playerInfo?.videoData?.video_id;
            const backups: string[] = nowPlaying.song.backups ?? [];
            const fvdIndex: number = backups.indexOf(fvd);

            if (fvd && backups.length && (fvd === nowPlaying.song.id || fvdIndex < backups.length - 1)) {
                mediaSource.loadVideoById(backups[fvdIndex + 1]);
            } else {
                handleNextPrevVideo(true);
            }
        }
    }

    const handleStatusChanged = async (ytStatus: number) => {
        let status: PlayerStatus = 'Buffering';
        switch (ytStatus) {
            case -1: // Unstarted
                status = 'Unstarted';
                break;
            case 0: // Ended
                status = 'Ended';
                handleNextPrevVideo(true);
                break;
            case 1: // Playing
                status = 'Playing';
                if(mediaSource) {
                    playerDispatch(updateTotalDuration(await mediaSource.getDuration()))
                }
                break;
            case 2: // Paused
            case 5: // Cued
                status = 'Paused'
                break;
            case 3: // Buffering
                status = 'Buffering';
                break;
            default:
                status = 'Unstarted'
        }

        playerDispatch(updateStatus(status));
    };

    React.useEffect(() => {
        // play/pause/seek are gated on isCasting because CastBridge forwards them
        // to the receiver while a session is active — without this guard the local
        // iframe and the TV would play in parallel. next/prev always run locally:
        // they mutate Redux nowPlaying, which CastBridge mirrors to the receiver.
        const unsubscribes = [
            PlayerEventBus.subscribe('play', () => {
                if (!isCastingRef.current) mediaSource?.playVideo();
            }),
            PlayerEventBus.subscribe('pause', () => {
                if (!isCastingRef.current) mediaSource?.pauseVideo();
            }),
            PlayerEventBus.subscribe('next', () => handleNextPrevVideo(true)),
            PlayerEventBus.subscribe('prev', () => handleNextPrevVideo(false)),
            PlayerEventBus.subscribe('seek', (seekTo) => {
                if (!isCastingRef.current) mediaSource?.seekTo(seekTo, true);
            }),
            // Cast just ended — seek the local iframe to where the TV left off.
            // Unconditional: only the bridge emits this, only on session end.
            PlayerEventBus.subscribe('castHandoff', (position) => {
                mediaSource?.seekTo(position, true);
            }),
        ];
        return () => {
            unsubscribes.forEach(unsubscribe => unsubscribe());
        }
    },[mediaSource, handleNextPrevVideo]);

    // Forward the mediaSource directly to the parent via ref
    React.useEffect(() => {
        if (ref && mediaSource) {
            (ref as React.MutableRefObject<{player: YouTubePlayer | null}>).current = {player: mediaSource};
        }
    }, [mediaSource, ref]);

    return (
        <div className={`${styles.mediaSource__container} ${isFullScreen ? styles.fullScreen : ''} ${hideVisuals ? styles.hiddenVisuals : ''}`}>
            <YouTube
                videoId="dQw4w9WgXcQ"
                onReady={(event) => {setMediaSource(event.target); handleStatusChanged(2)}}
                onStateChange={(event) => handleStatusChanged(event.data)}
                opts={options}
                onError={handlenError}
                className={styles.mediaSource__video}
            />
            {!hideVisuals && <>
                <div className={`${styles.mediaSource__overlay} ${!showOverlay ? styles.mediaSource__hidden : ''}`}>
                    {nowPlaying?.playlist.img && <img src={nowPlaying?.playlist.img || ''} alt={nowPlaying?.playlist.name || ''} className={styles.mediaSource__img}/>}
                </div>
                <div className={styles.mediaSource__trackDetails}>
                   <ScrollingText text={nowPlaying?.song?.name ?? ''} />
                    <div className={styles.mediaSource__artist}>{nowPlaying?.song?.artist}</div>
                </div>
            </>}
        </div>
    );
});

